import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { useQuery } from "@tanstack/react-query"
import { Button } from "design-system/atoms/Button"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { ChevronRight } from "lucide-react"
import { getBackofficeKnowledgeCategoryCompaniesQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeCategoryCompaniesQuery"
import { useKnowledgeTableSort } from "modules/backoffice/lib/knowledge/useKnowledgeTableSort"
import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeKnowledgeCategoriesResponseDataSchema,
  GetBackofficeKnowledgeMetricsSchema,
} from "schema"
import { z } from "zod"
import { BackofficeKnowledgeComparisonCells } from "./BackofficeKnowledgeComparisonCells"
import { BackofficeKnowledgeSection } from "./BackofficeKnowledgeSection"

type Categories = z.infer<typeof BackofficeKnowledgeCategoriesResponseDataSchema>
type Props = {
  data: Categories | undefined
  isPending: boolean
  query: z.input<typeof GetBackofficeKnowledgeMetricsSchema>
}
const COLUMN_COUNT = 7

export function BackofficeKnowledgeCategoriesTable({ data, isPending, query }: Props) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<string>()
  const cohortQuery = { ...query }
  delete cohortQuery.companyId
  const breakdown = useQuery(
    getBackofficeKnowledgeCategoryCompaniesQuery(
      { ...cohortQuery, category: expanded ?? "" },
      expanded !== undefined,
    ),
  )
  const { sortedData, sortable } = useKnowledgeTableSort(data, {
    category: (row) => row.category,
    questionCount: (row) => row.questionCount,
    questionShare: (row) => row.questionShare,
    answeredRate: (row) => row.answeredRate,
    score: (row) => row.score,
    knowledgePendingCount: (row) => row.knowledgePendingCount,
    evolution: (row) => row.answeredRateChangePercentagePoints,
  })

  function toggle(category: string) {
    setExpanded((current) => (current === category ? undefined : category))
  }

  function renderBreakdown() {
    if (breakdown.isPending) return <TableSkeleton colSpans={Array(COLUMN_COUNT).fill(1)} />
    if (breakdown.isError)
      return (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT}>
            <div className="flex items-center gap-2" role="alert">
              {t("backoffice.knowledge.categories.loadError")}
              <Button variant="outline" size="sm" onClick={() => breakdown.refetch()}>
                {t("backoffice.knowledge.categories.retry")}
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )
    if (!breakdown.data?.length)
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.knowledge.companies.empty")}
        />
      )

    return breakdown.data.map((row) => (
      <TableRow key={row.companyId ?? "remainder"} className="bg-muted/30">
        <TableCell className="pl-10">
          {row.companyName ?? t("backoffice.knowledge.categories.remainder")}
        </TableCell>
        <BackofficeKnowledgeComparisonCells row={row} showShare />
      </TableRow>
    ))
  }

  function renderContent() {
    if (isPending || data === undefined)
      return <TableSkeleton colSpans={Array(COLUMN_COUNT).fill(1)} />
    if (!data.length)
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.knowledge.categories.empty")}
        />
      )

    return sortedData.map((row) => {
      const category = row.category ?? ""
      const isExpanded = expanded === category
      return (
        <Fragment key={category}>
          <TableRow
            onClick={() => toggle(category)}
            className={cn("cursor-pointer", isExpanded && "bg-muted")}
          >
            <TableCell>
              <button
                type="button"
                aria-expanded={isExpanded}
                className="flex items-center gap-2 text-left"
                onClick={(event) => {
                  event.stopPropagation()
                  toggle(category)
                }}
              >
                <ChevronRight
                  className={cn("size-4 shrink-0 transition-transform", isExpanded && "rotate-90")}
                />
                {row.category ?? t("backoffice.knowledge.uncategorized")}
              </button>
            </TableCell>
            <BackofficeKnowledgeComparisonCells row={row} showShare />
          </TableRow>
          {isExpanded && (
            <>
              <TableRow className="bg-muted/30">
                <TableCell colSpan={COLUMN_COUNT} className="text-xs text-muted-foreground">
                  {t("backoffice.knowledge.categories.cohortHelp")}
                </TableCell>
              </TableRow>
              {renderBreakdown()}
            </>
          )}
        </Fragment>
      )
    })
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.knowledge.categories.title")}
      description={t("backoffice.knowledge.categories.description")}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead sortable={sortable("category")}>
              {t("backoffice.knowledge.table.category")}
            </TableHead>
            <TableHead align="right" sortable={sortable("questionCount")}>
              {t("backoffice.knowledge.metrics.questionCount")}
            </TableHead>
            <TableHead align="right" sortable={sortable("questionShare")}>
              {t("backoffice.knowledge.metrics.questionShare")}
            </TableHead>
            <TableHead align="right" sortable={sortable("answeredRate")}>
              {t("backoffice.knowledge.metrics.answeredRate")}
            </TableHead>
            <TableHead
              align="right"
              title={t("backoffice.knowledge.metrics.scoreHelp")}
              sortable={sortable("score")}
            >
              {t("backoffice.knowledge.metrics.score")}
            </TableHead>
            <TableHead align="right" sortable={sortable("knowledgePendingCount")}>
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <span tabIndex={0}>
                    {t("backoffice.knowledge.metrics.knowledgePendingCount")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("backoffice.knowledge.metrics.knowledgePendingHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right" sortable={sortable("evolution")}>
              <Tooltip>
                <TooltipTrigger asChild slick>
                  <span tabIndex={0}>{t("backoffice.knowledge.evolution.title")}</span>
                </TooltipTrigger>
                <TooltipContent>{t("backoffice.knowledge.evolution.help")}</TooltipContent>
              </Tooltip>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </BackofficeKnowledgeSection>
  )
}
